import React, { useEffect, useState, useContext } from "react";

import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import {
  BulletinShareListItem,
  getGroupBulletinShares,
  postBulletinShareAdmit,
  postBulletinShareReject,
} from "@rubi/api-generated";
import { useParams, useNavigate } from "react-router-dom";

import BulletinShareCard from "@/components/alerts/BulletinShare/BulletinShareCard";
import { AuthStateContext } from "@/contexts/AuthContext";

const AlertGroupBulletinSharePending: React.FC = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = useContext(AuthStateContext);
  const [shares, setShares] = useState<BulletinShareListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    getGroupBulletinShares({ path: { group_id: Number(groupId) } })
      .then((res) => {
        setShares(res.data?.bulletin_shares || []);
        setError(null);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load pending bulletin shares");
      })
      .finally(() => setLoading(false));
  }, [groupId]);

  // Optional: permission check, redirect if not allowed
  // TODO: check here to see if rerender won't stop
  const canManageBulletinSharing = checkPermission(
    "manage",
    "bulletin_sharing",
    Number(groupId),
  );
  if (!canManageBulletinSharing) {
    navigate("/rubialert/alerts");
    return null;
  }

  const handleAdmit = (id: number) => {
    postBulletinShareAdmit({ path: { bulletin_share_id: id } }).then(() => {
      setShares((prev) =>
        prev.filter((share) => share.bulletin_share.id !== id),
      );
    });
  };
  const handleReject = (id: number) => {
    postBulletinShareReject({ path: { bulletin_share_id: id } }).then(() => {
      setShares((prev) =>
        prev.filter((share) => share.bulletin_share.id !== id),
      );
    });
  };

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", mt: 4 }}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<ArrowBack />}
        sx={{ mb: 2 }}
        onClick={() => navigate(`/rubialert/manage-groups/${groupId}/`)}
      >
        Back to Dashboard
      </Button>
      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: 600, letterSpacing: 1 }}
      >
        Pending Bulletin Shares
      </Typography>
      {shares.length === 0 ? (
        <Typography
          variant="body1"
          align="center"
          sx={{ color: "text.secondary", mt: 4 }}
        >
          No pending shares available.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            justifyContent: "flex-start",
          }}
        >
          {shares.map((share) => (
            <Box
              key={share.bulletin_share.id}
              sx={{
                flex: "1 1 calc(25% - 24px)",
                minWidth: 260,
                maxWidth: 340,
                display: "flex",
              }}
            >
              <BulletinShareCard
                id={share.bulletin_share.id}
                bulletin_id={share.bulletin_share.bulletin_id}
                source_user={share.bulletin_share.source_user}
                source_org={share.bulletin_share.source_org}
                destination_group={share.bulletin_share.destination_group}
                bulletin={share.bulletin_share.bulletin as any}
                time_expires={share.bulletin_share.time_expires}
                onAdmit={handleAdmit}
                onReject={handleReject}
                is_moderation_enabled={
                  share.destination_group_membership
                    .moderate_bulletin_admittance
                }
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AlertGroupBulletinSharePending;
